import React from 'react';

import { AdaptiveSingleLineText, StatusTag } from '@components/common';
import CategoriesList from '@components/common/CategoriesList';
import TextAvatar from '@components/common/TextAvatar/TextAvatar';

import { formatDate } from '@utils/date';

import { ITitle } from '@interfaces/common/common';
import { IUserProfile } from '@interfaces/user/userProfile';

import { EStatus } from '@enums/common';

import { LINE_WIDTH } from './constants';

import styles from './OrganisationShortInfo.module.scss';

interface IOrganisationShortInfoProps {
	userProfile: IUserProfile;
}

const OrganisationShortInfo = ({ userProfile: { organization } }: IOrganisationShortInfoProps) => (
	<div className={styles.organisationShortInfoContainer}>
		<div className={styles.header}>
			<TextAvatar
				avatarId={organization?.avatar?.id}
				className={styles.logo}
				fontSize='20px'
				fullName={organization.name}
				id={organization.id}
				size='70px'
			/>
			<div>
				<div className={styles.name}>{organization.name}</div>
				<div className={styles.organisationType}>
					{organization.types.map(({ title }: ITitle) => title).join(', ')}
				</div>
				<div className={styles.status}>
					<StatusTag status={organization.isActive ? EStatus.active : EStatus.blocked} />
				</div>
			</div>
		</div>
		<div className={styles.infoSection}>
			<div className={styles.title}>Basic information</div>
			<hr />
			<div className={styles.infoItem}>
				<div className={styles.label}>Legal name</div>
				<div className={styles.value}>{organization.legalName}</div>
			</div>
			{organization.websiteURL && (
				<div className={styles.infoItem}>
					<div className={styles.label}>Website URL</div>
					<a
						className={styles.value}
						href={organization.websiteURL}
						rel='noreferrer'
						target='_blank'
					>
						<AdaptiveSingleLineText
							maxWidth={LINE_WIDTH}
							paddingsSize={0}
							styleInline={{ padding: '0' }}
							text={organization.websiteURL}
						/>
					</a>
				</div>
			)}
			<div className={styles.infoItem}>
				<div className={styles.label}>Added date</div>
				<div className={styles.value}>{formatDate(organization.createdAt)}</div>
			</div>
			<div className={styles.infoItem}>
				<div className={styles.label}>Updated date</div>
				<div className={styles.value}>{formatDate(organization.updatedAt)}</div>
			</div>
			{!!organization.categories.length && (
				<div className={styles.infoItem}>
					<div className={styles.label}>Categories</div>
					<div className={styles.value}>
						<CategoriesList categories={organization.categories} />
					</div>
				</div>
			)}
		</div>
		<div className={styles.infoSection}>
			<div className={styles.title}>Address</div>
			<hr />
			{organization.nameProperty && (
				<div className={styles.infoItem}>
					<div className={styles.label}>Property No./Name</div>
					<div className={styles.value}>{organization.nameProperty}</div>
				</div>
			)}
			{organization.streetName && (
				<div className={styles.infoItem}>
					<div className={styles.label}>Street Name</div>
					<div className={styles.value}>{organization.streetName}</div>
				</div>
			)}
			{organization.locality && (
				<div className={styles.infoItem}>
					<div className={styles.label}>Locality</div>
					<div className={styles.value}>{organization.locality}</div>
				</div>
			)}
			{organization.city && (
				<div className={styles.infoItem}>
					<div className={styles.label}>Town/City</div>
					<div className={styles.value}>{organization.city}</div>
				</div>
			)}
			<div className={styles.infoItem}>
				<div className={styles.label}>Postcode</div>
				<div className={styles.value}>{organization.postcode}</div>
			</div>
			<div className={styles.infoItem}>
				<div className={styles.label}>Country</div>
				<div className={styles.value}>{organization.country.name}</div>
			</div>
		</div>
	</div>
);

export default OrganisationShortInfo;
